import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CheckCircle, XCircle, Clock, ExternalLink, Search, FileText } from "lucide-react";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";

interface CreatorApplication {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  socialLink: string | null;
  instagramHandle: string | null;
  tiktokHandle: string | null;
  youtubeHandle: string | null;
  estimatedAudience: string | null;
  referralCode: string | null;
  status: string;
  adminNotes: string | null;
  reviewedAt: string | null;
  setupToken: string | null;
  setupTokenUsed: boolean | null;
  createdAt: string;
}

export default function AdminCreatorApplications() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApp, setSelectedApp] = useState<CreatorApplication | null>(null);
  const [adminNotes, setAdminNotes] = useState("");

  const { data, isLoading } = useQuery<{ applications: CreatorApplication[] }>({
    queryKey: ["/api/admin/creator-applications"],
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      return await apiRequest("PATCH", `/api/admin/creator-applications/${id}`, {
        status: "approved",
        adminNotes: notes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/creator-applications"] });
      toast({ title: "Application approved", description: "An approval email with setup link has been sent." });
      setSelectedApp(null);
      setAdminNotes("");
    },
    onError: (error: any) => {
      toast({ title: "Failed to approve", description: error.message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      return await apiRequest("PATCH", `/api/admin/creator-applications/${id}`, {
        status: "rejected",
        adminNotes: notes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/creator-applications"] });
      toast({ title: "Application rejected" });
      setSelectedApp(null);
      setAdminNotes("");
    },
    onError: (error: any) => {
      toast({ title: "Failed to reject", description: error.message, variant: "destructive" });
    },
  });

  const applications = data?.applications || [];
  const filtered = applications.filter((app) => {
    const matchesStatus = statusFilter === "all" || app.status === statusFilter;
    const matchesSearch = searchQuery === "" ||
      `${app.firstName} ${app.lastName} ${app.email}`.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const counts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-chart-2 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getSocialLinks = (app: CreatorApplication) => {
    const links: { label: string; url: string }[] = [];
    if (app.socialLink) {
      const url = app.socialLink.startsWith('http') ? app.socialLink : `https://${app.socialLink}`;
      links.push({ label: app.socialLink, url });
    }
    if (app.instagramHandle) {
      const handle = app.instagramHandle.replace('@', '');
      links.push({ label: `IG: @${handle}`, url: `https://instagram.com/${handle}` });
    }
    if (app.tiktokHandle) {
      const handle = app.tiktokHandle.replace('@', '');
      links.push({ label: `TT: @${handle}`, url: `https://tiktok.com/@${handle}` });
    }
    if (app.youtubeHandle) {
      const handle = app.youtubeHandle.replace('@', '');
      links.push({ label: `YT: @${handle}`, url: `https://youtube.com/@${handle}` });
    }
    return links;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Creator Applications</h1>
        <p className="text-muted-foreground">Review and manage content creator applications</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s]})
            </Button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading applications...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
            <FileText className="w-8 h-8 text-muted-foreground" />
            <p className="text-muted-foreground">No applications found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((app) => (
            <Card key={app.id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">{app.firstName} {app.lastName}</h3>
                      {getStatusBadge(app.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">{app.email}</p>
                    {app.phoneNumber && (
                      <p className="text-sm text-muted-foreground">Phone: {app.phoneNumber}</p>
                    )}
                    {getSocialLinks(app).length > 0 ? (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        {getSocialLinks(app).map((link, i) => (
                          <a
                            key={i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1 break-all"
                          >
                            <ExternalLink className="w-3 h-3 shrink-0" />
                            {link.label}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No social links</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Applied: {formatDate(app.createdAt)}
                    </p>
                    {app.adminNotes && (
                      <p className="text-xs text-muted-foreground mt-1">Notes: {app.adminNotes}</p>
                    )}
                    {app.status === 'approved' && app.setupTokenUsed && (
                      <p className="text-xs text-chart-2">Account setup completed</p>
                    )}
                    {app.status === 'approved' && !app.setupTokenUsed && (
                      <p className="text-xs text-amber-600">Awaiting account setup</p>
                    )}
                  </div>
                  {app.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedApp(app);
                          setAdminNotes("");
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedApp} onOpenChange={(open) => !open && setSelectedApp(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Application</DialogTitle>
            <DialogDescription>
              {selectedApp?.firstName} {selectedApp?.lastName} - {selectedApp?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Social Links</Label>
              {selectedApp && getSocialLinks(selectedApp).length > 0 ? (
                <div className="flex flex-col gap-1 mt-1">
                  {getSocialLinks(selectedApp).map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1 break-all"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      {link.label}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">No social links provided</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Admin Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this application..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="destructive"
              onClick={() => selectedApp && rejectMutation.mutate({ id: selectedApp.id, notes: adminNotes })}
              disabled={rejectMutation.isPending || approveMutation.isPending}
            >
              {rejectMutation.isPending ? "Rejecting..." : "Reject"}
            </Button>
            <Button
              onClick={() => selectedApp && approveMutation.mutate({ id: selectedApp.id, notes: adminNotes })}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              {approveMutation.isPending ? "Approving..." : "Approve & Send Setup Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
